import React, { useEffect, useRef, useState } from "react";
import "./DurationInput.css";
import TimerModal from "./TimerModal";
import { ClockIcon } from '@heroicons/react/24/solid';
import {
  workoutSetEditingRegistry,
  WORKOUT_COACH_CLEARANCE_SETTLE_MS,
} from './floatingControlRegistry';
import { suppressNumericInputContextMenu } from './numericInputContextMenu';

export const DurationInput = ({ value, onChange, editable, modalService, onAccept = null, enableTimer = true, allowTimerWhenReadOnly = false, isCurrentSet = false, imageSrc, title }) => {
  const [editingField, setEditingField] = useState(null);
  const [isCoachClearanceSettling, setIsCoachClearanceSettling] = useState(false);
  const coachClearanceOwnerRef = useRef(Symbol('duration-input-coach-clearance'));
  const coachClearanceTimeoutRef = useRef(null);

  useEffect(() => {
    workoutSetEditingRegistry.setActive(
      coachClearanceOwnerRef.current,
      editingField !== null || isCoachClearanceSettling
    );
  }, [editingField, isCoachClearanceSettling]);

  useEffect(() => () => {
    if (coachClearanceTimeoutRef.current) clearTimeout(coachClearanceTimeoutRef.current);
    workoutSetEditingRegistry.setActive(coachClearanceOwnerRef.current, false);
  }, []);

  const beginCoachClearance = () => {
    if (coachClearanceTimeoutRef.current) clearTimeout(coachClearanceTimeoutRef.current);
    coachClearanceTimeoutRef.current = null;
    setIsCoachClearanceSettling(false);
    workoutSetEditingRegistry.setActive(coachClearanceOwnerRef.current, true);
  };

  const totalMilliseconds = value || 0;
  const totalSeconds = Math.floor(totalMilliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const update = (h, m, s) => {
    const total = (h * 3600 + m * 60 + s) * 1000;
    onChange(total);
  };

  const handleFieldClick = (field) => {
    beginCoachClearance();
    setEditingField(field);
  };

  const handleBlur = (e, h, m, s, field) => {
    if (e.target.value === "") {
      e.target.value = 0;
      if (field === "hours") update(0, m, s);
      if (field === "minutes") update(h, 0, s);
      if (field === "seconds") update(h, m, 0);
    }
    setEditingField(null);
    setIsCoachClearanceSettling(true);
    if (coachClearanceTimeoutRef.current) clearTimeout(coachClearanceTimeoutRef.current);
    coachClearanceTimeoutRef.current = setTimeout(() => {
      coachClearanceTimeoutRef.current = null;
      setIsCoachClearanceSettling(false);
    }, WORKOUT_COACH_CLEARANCE_SETTLE_MS);
  };

  const handleInputChange = (e, h, m, s, field) => {
    const rawValue = e.target.value;
    const value = rawValue.replace(/^0+/, "");
    e.target.value = value;
    if (field === "hours") update(+value, m, s);
    if (field === "minutes") update(h, +value, s);
    if (field === "seconds") update(h, m, +value);
  };

  const handleFocus = (e) => {
    beginCoachClearance();
    e.target.select();
  };

  const openTimerModal = () => {
    modalService.open(TimerModal, {
      initialValue: value,
      onApply: (newValue) => {
        if (onAccept) onAccept(newValue);
      },
      startImmediately: true,
      imageSrc,
      title
    }, { layout: 'edge-to-edge' });
  };

  return (
    <div className="duration-input">
      {editable ? (
        <div className="duration-input-container">
          <div className="duration-input-row">
            <div className="duration-input-values">
            <label>
              {editingField === "hours" ? (
                <input
                  type="number"
                  className="duration-input-field"
                  value={hours}
                  min="0"
                  onFocus={handleFocus}
                  onContextMenu={suppressNumericInputContextMenu}
                  onChange={(e) => handleInputChange(e, hours, minutes, seconds, "hours")}
                  onBlur={(e) => handleBlur(e, hours, minutes, seconds, "hours")}
                />
              ) : (
                <span
                  className="duration-display"
                  onClick={() => handleFieldClick("hours")}
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  {hours}h
                </span>
              )}
            </label>
            <label>
              {editingField === "minutes" ? (
                <input
                  type="number"
                  className="duration-input-field"
                  value={minutes}
                  onFocus={handleFocus}
                  onContextMenu={suppressNumericInputContextMenu}
                  onChange={(e) => handleInputChange(e, hours, minutes, seconds, "minutes")}
                  onBlur={(e) => handleBlur(e, hours, minutes, seconds, "minutes")}
                />
              ) : (
                <span
                  className="duration-display"
                  onClick={() => handleFieldClick("minutes")}
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  {minutes}m
                </span>
              )}
            </label>
            <label>
              {editingField === "seconds" ? (
                <input
                  type="number"
                  className="duration-input-field"
                  value={seconds}
                  onFocus={handleFocus}
                  onContextMenu={suppressNumericInputContextMenu}
                  onChange={(e) => handleInputChange(e, hours, minutes, seconds, "seconds")}
                  onBlur={(e) => handleBlur(e, hours, minutes, seconds, "seconds")}
                />
              ) : (
                <span
                  className="duration-display"
                  onClick={() => handleFieldClick("seconds")}
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  {seconds}s
                </span>
              )}
            </label>
            </div>
            {editable && enableTimer && (
              <button
                type="button"
                aria-label="Start set timer"
                className={`clockwatch-button${isCurrentSet ? ' current-set-timer' : ''}`}
                onClick={openTimerModal}
              >
                <ClockIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="duration-input-row">
          <span>
            {hours > 0
              ? `${hours}h ${minutes}m ${seconds}s`
              : minutes > 0
              ? `${minutes}m ${seconds}s`
              : `${seconds}s`}
          </span>
          {allowTimerWhenReadOnly && enableTimer && (
            <button
              type="button"
              aria-label="Start set timer"
              className={`clockwatch-button${isCurrentSet ? ' current-set-timer' : ''}`}
              onClick={openTimerModal}
            >
              <ClockIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DurationInput;